import { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import TitleBar from "../../designSystem/c-organism/titleBar/TitleBar";
import Button from "../../designSystem/b-molecules/buttons/Button";
import Icon from "../../designSystem/a-atoms/icons/Icon";
import { importCSV, importGooglePlay } from "../../services/insightsApi";
import style from "./Insights.module.css";

const FIELD_HINTS = {
  text: {
    label: "Feedback Text",
    description: "The main feedback/review content",
    patterns: [
      "review text", "review_text", "reviewtext",
      "feedback text", "feedback_text", "feedbacktext",
      "comment", "comments", "text", "feedback", "review",
      "description", "message", "body", "content", "note", "notes",
    ],
  },
  date: {
    label: "Date",
    description: "When the feedback was submitted",
    patterns: [
      "review submit date and time", "review_submit_date_and_time",
      "review last update date and time",
      "date", "created_at", "createdat", "timestamp",
      "submitted_at", "submittedat", "submit date",
      "review date", "review_date", "reviewdate",
      "created", "created date",
    ],
  },
  rating: {
    label: "Rating",
    description: "Numeric rating (e.g. 1-5 stars)",
    patterns: [
      "star rating", "star_rating", "starrating",
      "rating", "score", "stars", "star",
      "overall rating", "overall_rating",
    ],
  },
};

function autoDetectMapping(headers) {
  const mapping = { text: "", date: "", rating: "" };
  const lowerHeaders = headers.map((h) => h.toLowerCase().trim());

  for (const [field, config] of Object.entries(FIELD_HINTS)) {
    for (const pattern of config.patterns) {
      const idx = lowerHeaders.indexOf(pattern);
      if (idx !== -1) {
        mapping[field] = headers[idx];
        break;
      }
    }
  }

  return mapping;
}

function detectSourceType(headers) {
  const lower = headers.map((h) => h.toLowerCase());
  if (lower.includes("package name") && lower.includes("star rating")) return "Google Play Export";
  if (lower.includes("app store") || lower.includes("app id")) return "App Store Export";
  if (lower.includes("ticket id") || lower.includes("case number")) return "Support Tickets";
  return "";
}

const InsightsImport = () => {
  const org = useSelector((state) => state.orgStatus.value);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState("csv");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [columnMapping, setColumnMapping] = useState({ text: "", date: "", rating: "" });
  const [csvSource, setCsvSource] = useState("");
  const industry = "Betting";
  const [geography, setGeography] = useState(
    () => localStorage.getItem("insights_geography") || "Nigeria"
  );

  const [packageName, setPackageName] = useState("");

  function handleFileSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    setCsvFile(file);
    setResult(null);
    setError(null);

    Papa.parse(file, {
      header: true,
      preview: 5,
      complete: (results) => {
        const headers = results.meta.fields;
        setCsvPreview({ headers, rows: results.data });

        const detected = autoDetectMapping(headers);
        setColumnMapping(detected);

        const detectedSource = detectSourceType(headers);
        if (detectedSource && !csvSource) setCsvSource(detectedSource);
      },
      error: () => setError("Failed to parse CSV file"),
    });
  }

  function updateMapping(field, value) {
    setColumnMapping((prev) => ({ ...prev, [field]: value }));
  }

  async function handleCSVImport() {
    if (!csvFile || !org?.payload) return;

    if (!columnMapping.text && !columnMapping.rating) {
      setError("Please map at least the Feedback Text or Rating column.");
      return;
    }

    setImporting(true);
    setError(null);
    setResult(null);

    try {
      const parseResult = await new Promise((resolve, reject) => {
        Papa.parse(csvFile, {
          header: true,
          complete: resolve,
          error: reject,
        });
      });

      const records = parseResult.data
        .filter((row) => {
          const text = columnMapping.text ? (row[columnMapping.text] || "").trim() : "";
          const rating = columnMapping.rating ? row[columnMapping.rating] : "";
          return text || rating;
        })
        .map((row) => ({
          text: columnMapping.text ? (row[columnMapping.text] || "") : "",
          date: columnMapping.date ? (row[columnMapping.date] || "") : "",
          rating: columnMapping.rating ? (row[columnMapping.rating] || null) : null,
        }));

      if (!records.length) {
        setError("No valid records found after applying the column mapping.");
        setImporting(false);
        return;
      }

      const res = await importCSV({
        orgId: org.payload,
        records,
        source: csvSource || csvFile.name,
        industry,
        geography,
      });

      setResult(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  }

  async function handleGooglePlayImport() {
    if (!packageName || !org?.payload) return;
    setImporting(true);
    setError(null);
    setResult(null);

    try {
      const res = await importGooglePlay({
        orgId: org.payload,
        packageName,
        industry,
        geography,
      });
      setResult(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  }

  const mappingComplete = columnMapping.text || columnMapping.rating;

  return (
    <div className="mainContentInner">
      <TitleBar
        title="Import Feedback"
        breadcrumb={[
          { name: "Home", url: "/" },
          { name: "Insights", url: "/insights" },
        ]}
      />

      <div className="contentPadding">
        <div className={style.importTabs}>
          <button
            className={`${style.importTab} ${activeTab === "csv" ? style.activeTab : ""}`}
            onClick={() => setActiveTab("csv")}
          >
            <Icon>description</Icon> CSV Upload
          </button>
          <button
            className={`${style.importTab} ${activeTab === "google-play" ? style.activeTab : ""}`}
            onClick={() => setActiveTab("google-play")}
          >
            <Icon>shop</Icon> Google Play
          </button>
        </div>

        <div className={style.contextFields}>
          <div className={style.fieldGroup}>
            <label className={style.fieldLabel}>Geography</label>
            <select
              className={style.filterSelect}
              value={geography}
              onChange={(e) => {
                setGeography(e.target.value);
                localStorage.setItem("insights_geography", e.target.value);
              }}
            >
              <option value="Nigeria">Nigeria</option>
              <option value="Zambia">Zambia</option>
              <option value="South Africa">South Africa</option>
            </select>
          </div>
        </div>

        {activeTab === "csv" && (
          <div className={style.importSection}>
            <div className={style.fieldGroup}>
              <label className={style.fieldLabel}>Source Name (optional)</label>
              <input
                type="text"
                className={style.textInput}
                placeholder="e.g. Customer Support Q1 2025"
                value={csvSource}
                onChange={(e) => setCsvSource(e.target.value)}
              />
            </div>

            <div
              className={style.dropZone}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
              <Icon>cloud_upload</Icon>
              <p>{csvFile ? csvFile.name : "Click to select a CSV file"}</p>
              <span className={style.dropZoneHint}>
                Any CSV format — you'll map columns in the next step
              </span>
            </div>

            {csvPreview && (
              <>
                <div className={style.mappingSection}>
                  <h4>Map Your Columns</h4>
                  <p className={style.mappingHint}>
                    We've auto-detected what we can. Adjust if needed.
                  </p>
                  <div className={style.mappingGrid}>
                    {Object.entries(FIELD_HINTS).map(([field, config]) => (
                      <div key={field} className={style.mappingRow}>
                        <div className={style.mappingLabel}>
                          <strong>{config.label}</strong>
                          {field === "text" && <span className={style.requiredBadge}>required</span>}
                          <div className={style.mappingDescription}>{config.description}</div>
                        </div>
                        <select
                          className={style.mappingSelect}
                          value={columnMapping[field]}
                          onChange={(e) => updateMapping(field, e.target.value)}
                        >
                          <option value="">— Not mapped —</option>
                          {csvPreview.headers.map((h) => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </select>
                        {columnMapping[field] && (
                          <Icon>check_circle</Icon>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className={style.previewSection}>
                  <h4>Preview ({csvPreview.rows.length} of total rows shown)</h4>
                  <div className={style.previewTable}>
                    <table>
                      <thead>
                        <tr>
                          {csvPreview.headers.map((h) => {
                            const mappedTo = Object.entries(columnMapping).find(([, v]) => v === h);
                            return (
                              <th key={h} className={mappedTo ? style.mappedColumn : ""}>
                                {h}
                                {mappedTo && (
                                  <span className={style.mappedBadge}>
                                    {FIELD_HINTS[mappedTo[0]].label}
                                  </span>
                                )}
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.rows.map((row, i) => (
                          <tr key={i}>
                            {csvPreview.headers.map((h) => {
                              const mappedTo = Object.entries(columnMapping).find(([, v]) => v === h);
                              return (
                                <td key={h} className={mappedTo ? style.mappedColumn : ""}>
                                  {(row[h] || "").substring(0, 80)}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <Button
                  onClick={handleCSVImport}
                  disabled={!mappingComplete || importing}
                  variants={["filled"]}
                  preIcon={importing ? "hourglass_empty" : "upload"}
                >
                  {importing ? "Importing & Analyzing..." : "Import CSV"}
                </Button>
              </>
            )}
          </div>
        )}

        {activeTab === "google-play" && (
          <div className={style.importSection}>
            <div className={style.fieldGroup}>
              <label className={style.fieldLabel}>Package Name</label>
              <input
                type="text"
                className={style.textInput}
                placeholder="e.g. com.example.myapp"
                value={packageName}
                onChange={(e) => setPackageName(e.target.value)}
              />
            </div>
            <p className={style.hint}>
              Requires Google Play Console API credentials configured on the server.
            </p>
            <Button
              onClick={handleGooglePlayImport}
              disabled={!packageName || importing}
              variants={["filled"]}
              preIcon={importing ? "hourglass_empty" : "sync"}
            >
              {importing ? "Syncing & Analyzing..." : "Sync Reviews"}
            </Button>
          </div>
        )}

        {error && (
          <div className={style.resultError}>
            <Icon>error</Icon> {error}
          </div>
        )}

        {result && (
          <div className={style.resultSuccess}>
            <Icon>check_circle</Icon>
            <div>
              <strong>Import complete!</strong>
              <p>{result.imported} records imported and categorized into {result.categories?.length || 0} categories.</p>
              <Button variants={["text"]} onClick={() => navigate("/insights")}>
                View Dashboard
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InsightsImport;
